"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function SportsClubAppointments({ section }) {
  return (
    <section className="py-10 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        
        {/* Header Block */}
        <div className="mb-8">
          <div className="pb-3 mb-2">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}
            >
              <span className="x_wd_display_fireworks_02">
                Score the perfect{" "}
                <span className="x_wd_yellow_highlight_bold_04 text-black px-2 rounded-md">
                  time slot
                </span>{" "}
                every single time
              </span>
            </motion.h2>
          </div>
        </div>

        {/* Desktop View (lg and up) - 100% Robust overlapping flexbox layout */}
        <div className="hidden lg:flex flex-row gap-12 relative min-h-[620px] items-start justify-between">
          
          {/* Left Column (55% width) - Text & Large Desktop Mockup */}
          <div className="w-[55%] flex flex-col gap-6 relative">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col gap-4"
            >
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text1 }} />
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text2 }} />

              {/* Bibi Persona Speech bubble (aligned on the left) */}
              <div className="flex items-center gap-3 pt-2 relative self-start z-40">
                <Image
                  src={section.personaAvatar}
                  width={40}
                  height={40}
                  alt="Bibi Persona"
                  className="w-10 h-10 rounded-full border border-white/10"
                />
                <div className="bg-white border border-gray-200 text-gray-800 rounded-full px-4 py-2 text-sm font-semibold shadow-md italic">
                  {section.personaText}
                </div>
              </div>
            </motion.div>

            {/* Desktop Dashboard Card overlapping into the right column */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-[130%] max-w-none mt-8 z-30 relative p-1.5 bg-white border border-gray-200 rounded-2xl shadow-2xl hover:scale-[1.01] transition-transform duration-300"
            >
              <Image
                src={section.imageLarge}
                width={800}
                height={500}
                alt="Sports Club Appointments Dashboard"
                className="w-full h-auto rounded-xl object-cover"
              />

              {/* Click Pointer Icon at the bottom center of the desktop card */}
              <motion.div
                initial={{ opacity: 0, scale: 0, y: 10 }}
                whileInView={{ opacity: 0.9, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 120, delay: 0.5 }}
                className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-14 h-auto z-40 pointer-events-none drop-shadow-2xl"
              >
                <Image
                  src={section.pointerIcon}
                  width={56}
                  height={56}
                  alt=""
                  className="w-full h-auto"
                />
              </motion.div>
            </motion.div>
          </div>

          {/* Right Column (40% width) - Mobile Calendar Mockup */}
          <div className="w-[40%] relative flex flex-col items-end">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="w-full p-1 bg-white border border-gray-200 rounded-[28px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 relative z-10"
            >
              <Image
                src={section.imageMobile}
                width={300}
                height={500}
                alt="Mobile Booking View"
                className="w-full h-auto rounded-[22px] object-cover"
              />
            </motion.div>
          </div>

          {/* Curved Arrow loop (Absolute-positioned between the two columns) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 0.4, scale: 1 }}
            viewport={{ once: true }}
            className="absolute top-[130px] left-[52%] z-20 pointer-events-none"
          >
            <Image
              src={section.loopArrowIcon}
              width={80}
              height={80}
              alt=""
              className="w-20 h-auto rotate-[80deg] scale-x-[-1]"
              style={{ filter: "invert(0.5) brightness(1.2)" }}
            />
          </motion.div>

        </div>

        {/* Mobile/Tablet View (below lg) - Clean stacked card layout */}
        <div className="lg:hidden flex flex-col gap-8 items-center w-full">
          <div className="space-y-4 w-full">
            <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text1 }} />
            <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text2 }} />
          </div>

          <div className="relative w-full flex flex-col gap-6 items-center">
            {/* Desktop Dashboard Card */}
            <div className="w-full p-1.5 bg-white border border-gray-200 rounded-2xl shadow-xl relative z-10">
              <Image
                src={section.imageLarge}
                width={800}
                height={500}
                alt="Sports Club Appointments Dashboard"
                className="w-full h-auto rounded-xl object-cover"
              />
              {/* Pointer Icon */}
              <Image
                src={section.pointerIcon}
                width={40}
                height={40}
                alt=""
                className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-10 h-auto z-20"
              />
            </div>

            {/* Mobile Calendar Card */}
            <div className="w-[60%] p-1 bg-white border border-gray-200 rounded-2xl shadow-xl mt-4">
              <Image
                src={section.imageMobile}
                width={300}
                height={500}
                alt="Mobile Booking View"
                className="w-full h-auto rounded-xl object-cover"
              />
            </div>
            
            {/* Bibi Persona Speech bubble */}
            <div className="flex items-center gap-3 pt-4">
              <Image
                src={section.personaAvatar}
                width={40}
                height={40}
                alt="Bibi Persona"
                className="w-10 h-10 rounded-full border border-white/10"
              />
              <div className="bg-white border border-gray-200 text-gray-800 rounded-full px-4 py-2 text-sm font-semibold shadow-md italic">
                {section.personaText}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SportsClubeCommerce({ section }) {
  return (
    <section className="py-10 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        
        {/* Centered Heading */}
        <div className="text-center max-w-4xl mx-auto mb-10 relative">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}
          >
            Gear up for{" "}
            <span className="x_wd_red_highlight_03">
              greatness
            </span>
          </motion.h2>
        </div>

        {/* Copywriting Intro layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 relative mb-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text1 }} />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text2 }} />
          </motion.div>
        </div>

        {/* Double Overlapping Dashboard Showcase */}
        <div className="relative w-full max-w-[950px] mx-auto flex flex-col items-center py-6 min-h-[400px] lg:min-h-[480px]">
          
          {/* Decorative Backdrop Circle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 aspect-square w-[75%] max-w-[500px] bg-white/[0.03] rounded-full z-0 pointer-events-none select-none" />

          {/* Background Mockup (Website Editor) with corner sparks */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="x_wd_corner_highlight_03 absolute top-0 w-[94%] z-10"
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-2xl shadow-xl">
              <Image
                src={section.imageLarge1}
                width={900}
                height={600}
                alt="Odoo AI Website Builder"
                className="w-full h-auto rounded-xl object-cover"
              />
            </div>
          </motion.div>

          {/* Foreground Mockup (eCommerce Products List) overlapping on top */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="w-full mt-[40px] md:mt-[60px] lg:mt-[80px] p-1.5 bg-white border border-gray-200 rounded-2xl shadow-2xl z-20 hover:scale-[1.01] transition-transform duration-300 relative"
          >
            <Image
              src={section.imageLarge2}
              width={900}
              height={600}
              alt="eCommerce Products Storefront"
              className="w-full h-auto rounded-xl object-cover"
            />
          </motion.div>

        </div>

        {/* Speech bubble */}
        <div className="flex justify-center pt-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-3 relative z-30"
          >
            <Image
              src={section.personaAvatar}
              width={40}
              height={40}
              alt="Bibi Persona"
              className="w-10 h-10 rounded-full border border-white/10"
            />
            <div className="bg-white border border-gray-200 text-gray-800 rounded-full px-4 py-2 text-sm font-semibold shadow-md italic">
              {section.personaText}
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
}

export function SportsClubPOS({ section }) {
  return (
    <section className="py-10 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        
        {/* Floating Splash Graphics */}
        <div className="absolute top-[10%] left-[20%] z-0 pointer-events-none opacity-20">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 relative"
          >
            <Image
              src={section.waterIcon}
              fill
              className="object-contain"
              alt=""
            />
          </motion.div>
        </div>
        <div className="absolute bottom-[20%] right-[30%] z-0 pointer-events-none opacity-20">
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 relative"
          >
            <Image
              src={section.waterIcon}
              fill
              className="object-contain"
              alt=""
            />
          </motion.div>
        </div>

        {/* Elegant Dark Glassmorphic Card Container Box with moderate padding */}
        <div className="relative w-full rounded-[36px] overflow-hidden bg-[#121115]/90 border border-white/15 p-6 sm:p-8 lg:p-10 shadow-2xl backdrop-blur-md relative z-10">
          <div className="absolute inset-0 bg-[#3b82f6]/5 rounded-full blur-[140px] pointer-events-none -z-10" />

          {/* Double Column layout with clean gap */}
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center justify-between relative z-10">
            
            {/* Left Column: Copywriting */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col gap-5 w-full lg:w-[48%]"
            >
              <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}>
                Game on,{" "}
                <span className="relative inline-block text-sky-400 font-bold">
                  <span className="relative z-10">drinks up!</span>
                  <svg
                    className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                      stroke="#38bdf8"
                      strokeWidth="3.5"
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </h2>

              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text }} />

              {/* Bibi Speech bubble */}
              <div className="flex items-center gap-3 pt-4 relative self-start">
                
                {/* Loop curved arrow */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, rotate: 100 }}
                  whileInView={{ opacity: 0.4, scale: 1, rotate: 120 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 100, delay: 0.3 }}
                  className="hidden sm:block absolute bottom-full right-full mb-2 mr-2 w-20 h-auto select-none pointer-events-none scale-x-[-1] flip"
                  style={{ filter: "invert(0.5) brightness(1.2)" }}
                >
                  <Image
                    src={section.loopArrowIcon}
                    width={80}
                    height={80}
                    className="w-full h-auto"
                    alt=""
                  />
                </motion.div>
                
                <Image
                  src={section.personaAvatar}
                  width={40}
                  height={40}
                  alt="Bibi Persona"
                  className="w-10 h-10 rounded-full border border-white/10"
                />
                <div className="bg-white border border-gray-200 text-gray-800 rounded-full px-4 py-2 text-sm font-semibold shadow-md italic">
                  {section.personaText}
                </div>
              </div>
            </motion.div>

            {/* Right Column: POS Device Showcase */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="w-full lg:w-[48%] p-1 bg-white border border-gray-200 rounded-2xl shadow-xl hover:scale-[1.01] transition-transform duration-300"
            >
              <Image
                src={section.image}
                width={600}
                height={400}
                className="w-full h-auto rounded-xl object-cover"
                alt="Odoo Point of Sale tablet view"
              />
            </motion.div>

          </div>
        </div>

      </div>
    </section>
  );
}
